import React from 'react';
import {
  Alert,
  Box,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useFetchRagProductCoverage } from '../../../../hooks/rag-hooks';
import { dateLabel } from './ragAdminUtils';

const RagCoverageReportPanel: React.FC = () => {
  const query = useFetchRagProductCoverage(
    {},
    {
      refetchInterval: 60000,
    },
  );

  const data = query.data;
  const totals = data?.totals || {};
  const rows = data?.items || [];

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          Cisco Product RAG Coverage
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Reports which Cisco products have active RAG mappings for chat and doc-export ingestion.
        </Typography>
      </Paper>

      {query.isLoading || query.isFetching ? <LinearProgress /> : null}

      {query.error ? (
        <Alert severity="error">
          {(query.error as Error)?.message || 'Failed to load product coverage. Ensure the backend endpoint exists.'}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12} md={2.4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Cisco Products
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {totals.cisco_products ?? rows.length}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Mapped
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {totals.mapped ?? '-'}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Unmapped
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {totals.unmapped ?? '-'}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Chat Mapped
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {totals.chat_mapped ?? '-'}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Doc Export Mapped
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {totals.doc_export_mapped ?? '-'}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell width={120}>Mapped</TableCell>
                <TableCell>Chat Pipeline</TableCell>
                <TableCell>Doc Export Pipeline</TableCell>
                <TableCell width={160}>Slots</TableCell>
                <TableCell width={180}>Last Ingestion</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((row: any) => (
                <TableRow key={row.product_id || row.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>
                      {row.product_name || row.name || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {row.product_id || row.id || '-'} / Offer: {row.offer_type || '-'} / Version:{' '}
                      {row.recommended_version || '-'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      color={row.mapped ? 'success' : 'error'}
                      variant="outlined"
                      label={row.mapped ? 'mapped' : 'unmapped'}
                    />
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {row.chat_pipeline_group || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                      {row.chat_pipeline_id || '-'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {row.doc_export_pipeline_group || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                      {row.doc_export_pipeline_id || '-'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" display="block">
                      Active: {row.active_slot || '-'}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Ingest: {row.ingest_slot || '-'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" display="block">
                      {row.last_ingestion_status || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dateLabel(row.last_ingestion_at)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {!rows.length && !query.isLoading ? (
          <Typography variant="body2" color="text.secondary" mt={2}>
            No coverage rows returned.
          </Typography>
        ) : null}
      </Paper>
    </Stack>
  );
};

export default RagCoverageReportPanel;